import {match} from 'ts-pattern';

import {useEffect} from 'react';

import {isNotNil} from 'ramda';

import {type EngagementReport} from '../../types/EngagementReport';
import {buildEngagementBeaconRequest} from '../../utils/buildEngagementBeaconRequest';
import {certificateClickBus} from '../../utils/certificateClickBus';
import {computeScrollDepthMilestone} from '../../utils/computeScrollDepthMilestone';
import {createEngagementTracker} from '../../utils/createEngagementTracker';

const VISIBILITY_CHANGE_EVENT = 'visibilitychange';
const PAGE_HIDE_EVENT = 'pagehide';
const PAGE_SHOW_EVENT = 'pageshow';
const SCROLL_EVENT = 'scroll';
const RESIZE_EVENT = 'resize';
const BLUR_EVENT = 'blur';
const FOCUS_EVENT = 'focus';
const POINTER_MOVE_EVENT = 'pointermove';
const KEY_DOWN_EVENT = 'keydown';
const CLICK_EVENT = 'click';
const HIDDEN_VISIBILITY_STATE = 'hidden';
const FULL_SCROLL_DEPTH_MILESTONE = 100;
/** A visible, focused tab with no user input for this long stops counting active time. */
const IDLE_TIMEOUT_MS = 60_000;
/** Upper bound on how often high-frequency input events re-arm the idle timer. */
const ACTIVITY_THROTTLE_MS = 1_000;
const PASSIVE_LISTENER = {passive: true} as const;
// Intentional: shared/web noop is any-typed.
const noop = () => undefined;

/**
 * `navigator.sendBeacon` sets its return value *before* the request is sent
 * (Beacon spec §3, step 7), so `true` means only "queued within the keepalive
 * quota" — never "delivered". An over-quota payload (`false`) or an unparseable
 * URL (`TypeError`) are the only failures a retry can help with, so they are the
 * only ones that fall through to the keepalive `fetch`.
 */
const queueBeacon = (request: ReturnType<typeof buildEngagementBeaconRequest>) => {
  try {
    return navigator.sendBeacon(request.url, request.blob);
  } catch {
    return false;
  }
};

export interface OnlineOfferEngagementTrackerProps {
  offerId: string;
  workspace: string;
  sessionId: string;
  engagementEndpoint: string;
  sendEngagement: EngagementTransport;
}

export type EngagementTransportArgs = EngagementReport & {
  offerId: string;
  workspace: string;
  sessionId: string;
};

export type EngagementTransport = (args: EngagementTransportArgs) => {
  unwrap: () => Promise<void>;
};

export function OnlineOfferEngagementTracker(props: OnlineOfferEngagementTrackerProps) {
  // useOnMount from shared/web has no cleanup arg, so this tracker uses raw useEffect.
  useEffect(
    function trackEngagement() {
      const tracker = createEngagementTracker({
        onHeartbeat: (report) =>
          props
            .sendEngagement({
              offerId: props.offerId,
              workspace: props.workspace,
              sessionId: props.sessionId,
              ...report,
            })
            .unwrap(),
        onFinalFlush: (report) => {
          const request = buildEngagementBeaconRequest({
            endpointUrl: props.engagementEndpoint,
            workspace: props.workspace,
            body: {
              sessionId: props.sessionId,
              ...report,
            },
          });

          if (queueBeacon(request)) {
            return;
          }

          fetch(request.url, {
            method: 'POST',
            body: request.blob,
            keepalive: true,
          }).catch(noop);
        },
      });

      let isIdle = false;
      let lastActivityAt: number | null = null;
      let idleTimeoutId: ReturnType<typeof setTimeout> | null = null;
      let scrollFrameId: number | null = null;

      const isEngaged = () =>
        document.visibilityState !== HIDDEN_VISIBILITY_STATE && document.hasFocus() && !isIdle;
      const syncActiveState = () => {
        if (isEngaged()) {
          tracker.resume();
          return;
        }

        tracker.pause();
      };
      const armIdleTimeout = () => {
        if (isNotNil(idleTimeoutId)) {
          clearTimeout(idleTimeoutId);
        }

        idleTimeoutId = setTimeout(() => {
          isIdle = true;
          tracker.pause();
        }, IDLE_TIMEOUT_MS);
      };
      const registerActivity = () => {
        isIdle = false;
        lastActivityAt = Date.now();
        armIdleTimeout();
        syncActiveState();
      };
      // Throttled entry point for high-frequency input events. Foreground
      // transitions call registerActivity directly so a quick blur/focus cycle
      // can never be throttled into leaving the tracker paused.
      const handleActivity = () => {
        const isThrottled =
          isNotNil(lastActivityAt) && Date.now() - lastActivityAt < ACTIVITY_THROTTLE_MS;

        if (isThrottled) {
          return;
        }

        registerActivity();
      };

      const measureScrollDepth = () => {
        scrollFrameId = null;

        const milestone = computeScrollDepthMilestone();
        tracker.updateScrollDepth(milestone);

        // The milestone is a monotonic ratchet, so once it saturates there is
        // nothing left to measure and the layout-reading listeners can go. Only
        // the measurement listeners are detached — scroll must keep counting as
        // activity, otherwise a reader scrolling back up a fully-seen page would
        // be treated as idle.
        if (milestone === FULL_SCROLL_DEPTH_MILESTONE) {
          detachScrollMeasurementListeners();
        }
      };
      // scrollHeight and scrollY force synchronous layout, so coalesce the reads
      // into one per animation frame instead of one per scroll event.
      const scheduleScrollMeasurement = () => {
        if (isNotNil(scrollFrameId)) {
          return;
        }

        scrollFrameId = requestAnimationFrame(measureScrollDepth);
      };

      function detachScrollMeasurementListeners() {
        window.removeEventListener(SCROLL_EVENT, scheduleScrollMeasurement);
        window.removeEventListener(RESIZE_EVENT, scheduleScrollMeasurement);
      }

      const pauseAndFlush = () => {
        tracker.pause();
        tracker.flushFinal();
      };
      const handleVisibilityChange = () => {
        match(document.visibilityState)
          .with(HIDDEN_VISIBILITY_STATE, pauseAndFlush)
          .otherwise(registerActivity);
      };
      const handlePageHide = () => pauseAndFlush();
      const unsubscribeCertificateClick = certificateClickBus.subscribe(
        tracker.reportCertificateClick
      );

      document.addEventListener(VISIBILITY_CHANGE_EVENT, handleVisibilityChange);
      window.addEventListener(PAGE_HIDE_EVENT, handlePageHide);
      window.addEventListener(PAGE_SHOW_EVENT, registerActivity);
      window.addEventListener(SCROLL_EVENT, scheduleScrollMeasurement, PASSIVE_LISTENER);
      window.addEventListener(RESIZE_EVENT, scheduleScrollMeasurement, PASSIVE_LISTENER);
      window.addEventListener(BLUR_EVENT, tracker.pause);
      window.addEventListener(FOCUS_EVENT, registerActivity);
      window.addEventListener(SCROLL_EVENT, handleActivity, PASSIVE_LISTENER);
      window.addEventListener(POINTER_MOVE_EVENT, handleActivity, PASSIVE_LISTENER);
      window.addEventListener(KEY_DOWN_EVENT, handleActivity);
      window.addEventListener(CLICK_EVENT, handleActivity);

      measureScrollDepth();
      tracker.start();
      armIdleTimeout();
      syncActiveState();

      return () => {
        document.removeEventListener(VISIBILITY_CHANGE_EVENT, handleVisibilityChange);
        window.removeEventListener(PAGE_HIDE_EVENT, handlePageHide);
        window.removeEventListener(PAGE_SHOW_EVENT, registerActivity);
        window.removeEventListener(BLUR_EVENT, tracker.pause);
        window.removeEventListener(FOCUS_EVENT, registerActivity);
        window.removeEventListener(SCROLL_EVENT, handleActivity);
        window.removeEventListener(POINTER_MOVE_EVENT, handleActivity);
        window.removeEventListener(KEY_DOWN_EVENT, handleActivity);
        window.removeEventListener(CLICK_EVENT, handleActivity);
        detachScrollMeasurementListeners();

        if (isNotNil(idleTimeoutId)) {
          clearTimeout(idleTimeoutId);
        }

        if (isNotNil(scrollFrameId)) {
          cancelAnimationFrame(scrollFrameId);
        }

        unsubscribeCertificateClick();
        pauseAndFlush();
        tracker.stop();
      };
    },
    [
      props.engagementEndpoint,
      props.offerId,
      props.sendEngagement,
      props.sessionId,
      props.workspace,
    ]
  );

  return null;
}
